import React from 'react';

function ContextBuilderContent() {
  return (
    <>
      <p>The Context Builder is a powerful AI-assisted feature that helps you automatically identify and select the most relevant files for your prompt based on your query and codebase analysis.</p>
      
      <h3>What is the Context Builder?</h3>
      <p>The Context Builder uses AI to analyze your codebase and your prompt, then recommends files organized by priority (high, medium, low) that are most relevant to your task. This helps you:</p>
      <ul>
        <li>Save time by automating file selection</li>
        <li>Identify files you might have overlooked</li>
        <li>Optimize token usage by focusing on the most relevant files</li>
        <li>Organize files by importance to better structure your context</li>
      </ul>
      
      <h3>Generating Recommendations</h3>
      <ol>
        <li>In RepoPrompt, navigate to the "Context Builder" tab</li>
        <li>The builder will use your prompt from the main instructions area (or an override prompt if specified)</li>
        <li>Select an AI model from the dropdown menu</li>
        <li>Click the "Generate" button to start analyzing your codebase</li>
        <li>A progress indicator will show the analysis stages</li>
        <li>Once complete, recommended files will appear organized by priority</li>
      </ol>
      
      <h3>Understanding Priorities</h3>
      <p>Recommended files are categorized into three priority levels:</p>
      <ul>
        <li><strong>High Priority (Red):</strong> Most critical files directly related to your task</li>
        <li><strong>Medium Priority (Orange):</strong> Supporting files that provide important context</li>
        <li><strong>Low Priority (Blue):</strong> Files with some relevance that could be helpful</li>
      </ul>
      <p>Each priority level has a toggle button allowing you to show or hide files of that priority. A counter shows how many files are in each category.</p>
      
      <h3>Token Estimation</h3>
      <p>The Context Builder provides token estimation to help you understand the impact on your token usage:</p>
      <ul>
        <li><strong>Tree:</strong> Tokens used by the file tree structure</li>
        <li><strong>Map:</strong> Tokens used by the code map</li>
        <li><strong>Prompt:</strong> Tokens used by your instructions and other text</li>
      </ul>
      <p>This helps you stay within token limits for your selected AI model.</p>
      
      <h3>Applying Recommendations</h3>
      <p>After generating recommendations, you have two ways to apply them:</p>
      <ul>
        <li><strong>Add:</strong> Adds recommended files to your existing selection</li>
        <li><strong>Replace:</strong> Replaces your current selection with the recommended files</li>
      </ul>
      <p>You can also remove individual files from the recommendations by clicking the 'X' button next to each file.</p>
      
      <h3>Context Controls</h3>
      <p>For advanced users, the Context Controls section offers additional configuration options:</p>
      <ul>
        <li><strong>Include File Tree:</strong> Toggle to include or exclude the file structure in analysis</li>
        <li><strong>Include Code Map:</strong> Toggle to include or exclude the code map in analysis</li>
        <li><strong>Parallel Queries:</strong> Enable to process larger codebases by splitting analysis into smaller chunks</li>
        <li><strong>Max Tokens per Query:</strong> When using Parallel Queries, adjust this slider to control the token limit for each query chunk</li>
        <li><strong>Override Prompt:</strong> Use a custom prompt specifically for the Context Builder instead of the main instructions</li>
      </ul>
      
      <h3>Model Considerations</h3>
      <p>Different AI models have different characteristics when used with the Context Builder:</p>
      <ul>
        <li><strong>GitHub-hosted models:</strong> Have tight rate limits and a maximum context input of 8K tokens, making them less suitable for the Context Builder</li>
        <li><strong>Gemini Pro 2.5:</strong> Works better with "Parallel Queries" disabled due to rate limit constraints</li>
        <li><strong>Empty Code Map:</strong> If your code map appears empty (very low token count), consider updating your File Settings to use Auto or Complete Code Map mode</li>
      </ul>
      
      <h3>Managing Recommendations</h3>
      <p>After generating recommendations, you can:</p>
      <ul>
        <li><strong>Save as Preset:</strong> Save the current recommendations as a named preset in your workspace</li>
        <li><strong>Revert:</strong> Return to your previous file selection (before applying recommendations)</li>
        <li><strong>Filter:</strong> Toggle between high, medium, and low priority files to focus on what's most important</li>
      </ul>
      
      <h3>Troubleshooting</h3>
      <p>If you encounter issues with the Context Builder:</p>
      <ul>
        <li>If an error occurs, check if your selected model has sufficient rate limits, especially when using parallel queries</li>
        <li>If no recommendations are found, ensure your code map is present and consider adjusting your prompt to be more specific</li>
        <li>If all recommendations are filtered out, enable at least one priority level (High, Medium, or Low)</li>
      </ul>
      
      <div className="tip-box">
        <strong>Tip:</strong> For optimal results, try different combinations of Context Controls settings. Smaller projects often work best with Parallel Queries disabled, while larger codebases may require it. Always use specific, detailed prompts about what you're trying to accomplish to help the AI better identify relevant files.
      </div>
    </>
  );
}

export default ContextBuilderContent;